import { motion } from "framer-motion";
import { Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { TESTIMONIALS, CASES } from "@/lib/constants";

export default function Testimonial() {
  const t = TESTIMONIALS[0];
  const linkedCase = CASES.find((c) => c.slug === t.caseSlug);

  return (
    <section
      id="testimonial"
      data-testid="testimonial-section"
      className="bg-white py-16 sm:py-28 relative overflow-hidden"
    >
      {/* Subtle backdrop text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.035]">
        <span
          className="font-serif text-[20vw] leading-none italic"
          style={{ fontFamily: "Fraunces, serif" }}
        >
          Cases
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT: image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-[#F8F8F8]">
              <img
                src={linkedCase.heroImage}
                alt={linkedCase.name}
                className="w-full h-full object-cover" loading="lazy" decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/40 via-transparent to-transparent" />
            </div>
            {/* Case label */}
            <div className="absolute -bottom-4 -right-4 sm:-right-6 bg-[#111111] text-white px-5 py-4 shadow-2xl max-w-[260px]">
              <div className="text-[9px] tracking-[0.32em] uppercase text-[#D4AF37]">
                Seneste case
              </div>
              <div
                className="font-serif text-xl sm:text-2xl leading-tight mt-1 font-light"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                {linkedCase.name}
              </div>
              <div className="text-[11px] text-white/60 mt-1 tracking-wide">
                {linkedCase.location}
              </div>
            </div>
            {/* Gold offset */}
            <div className="absolute -top-3 -left-3 -z-10 w-full h-full bg-[#D4AF37]/12" />
          </motion.div>

          {/* RIGHT: testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <p className="overline mb-5 text-[10px] sm:text-xs">
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Ord fra en boligejer
            </p>

            <Quote
              size={40}
              strokeWidth={1}
              className="text-[#D4AF37] mb-6 sm:mb-8"
            />

            <blockquote
              className="font-serif text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] text-[#111111] font-light leading-[1.25] tracking-tight"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              &ldquo;{t.quote}&rdquo;
            </blockquote>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 pt-8 border-t border-black/10">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center shrink-0 ring-1 ring-black/10">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      data-testid="testimonial-avatar"
                    />
                  ) : (
                    <span
                      className="font-serif text-2xl text-[#111111] font-medium"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      {t.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div>
                  <div
                    className="font-serif text-xl text-[#111111] font-normal leading-tight"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {t.name}
                  </div>
                  <div className="text-xs text-[#555555] mt-1 tracking-wide">
                    {t.role}
                  </div>
                </div>
              </div>

              <Link
                to={`/cases`}
                data-testid="testimonial-case-link"
                className="sm:ml-auto inline-flex items-center gap-3 text-xs tracking-[0.18em] uppercase text-[#111111] link-underline group"
              >
                <span>Se hele casen</span>
                <ArrowRight
                  size={16}
                  className="transition-transform duration-500 group-hover:translate-x-1"
                />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
